import * as Sentry from '@sentry/react';
import { isChunkReloadPending } from './utils/chunkReload';

// Get Sentry DSN from environment variable
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// A function call keeps TS from narrowing MODE to 'prod' in the options below.
const isProdBuild = () => import.meta.env.MODE === 'prod';

if (!SENTRY_DSN) {
  console.warn('VITE_SENTRY_DSN is not set. Sentry will not be initialized.');
} else if (!isProdBuild()) {
  console.log(
    `Sentry is disabled outside the prod build (mode: ${import.meta.env.MODE}).`
  );
} else {
  console.log(
    `Sentry DSN is set. Initializing Sentry for environment: ${import.meta.env.MODE}`
  );
}

const CLINICIAN_DIRECTORY_PATH = '/internal-tools/clinician-directory';
const SEARCH_PARAM = /([?&])q=[^&#]*/g;
// The same search as a JSON key, as in a serialized request config.
const SEARCH_KEY = /("q"\s*:\s*)"(?:[^"\\]|\\.)*"/g;
// Where a request config keeps its query string as an object.
const QUERY_HOLDERS = new Set(['query', 'params']);

/** A clinician directory search is a person's name or email: drop its value. */
export function scrubDirectorySearch(text: string): string {
  return text.includes(CLINICIAN_DIRECTORY_PATH)
    ? text
        .replace(SEARCH_PARAM, '$1q=[redacted]')
        .replace(SEARCH_KEY, '$1"[redacted]"')
    : text;
}

const isDirectoryRequest = (record: Record<string, unknown>) =>
  typeof record.url === 'string' &&
  record.url.includes(CLINICIAN_DIRECTORY_PATH);

/** Drop `q` from a request config's query object; it holds the bare search. */
function withoutSearch(item: unknown): unknown {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
  return 'q' in item ? { ...item, q: '[redacted]' } : item;
}

/**
 * Every string inside plain objects and arrays, scrubbed; depth-limited. In an
 * object whose `url` is a directory URL, `query.q` and `params.q` go too.
 */
function scrubDirectorySearchDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return scrubDirectorySearch(value);
  if (depth > 8 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map(item => scrubDirectorySearchDeep(item, depth + 1));
  }
  const record = value as Record<string, unknown>;
  const directory = isDirectoryRequest(record);
  return Object.fromEntries(
    Object.entries(record).map(([key, item]) => [
      key,
      scrubDirectorySearchDeep(
        directory && QUERY_HOLDERS.has(key) ? withoutSearch(item) : item,
        depth + 1
      ),
    ])
  );
}

function scrubDirectorySearches(event: Sentry.ErrorEvent): void {
  if (event.request?.url) {
    event.request.url = scrubDirectorySearch(event.request.url);
  }
  // Recursive over data: a console breadcrumb's arguments hold a request config URL.
  for (const crumb of event.breadcrumbs ?? []) {
    if (typeof crumb.message === 'string') {
      crumb.message = scrubDirectorySearch(crumb.message);
    }
    if (crumb.data) {
      crumb.data = scrubDirectorySearchDeep(crumb.data) as typeof crumb.data;
    }
  }
}

type RecordingEvent = Parameters<
  NonNullable<
    NonNullable<
      Parameters<typeof Sentry.replayIntegration>[0]
    >['beforeAddRecordingEvent']
  >
>[0];

/** Replay keeps its own network spans and breadcrumbs, which never reach beforeSend. */
export function scrubDirectoryRecordingEvent(
  event: RecordingEvent
): RecordingEvent {
  // rrweb DOM events carry no payload: leave them exactly as they are.
  const data: unknown = event.data;
  if (data && typeof data === 'object' && 'payload' in data) {
    const holder = data as { payload: unknown };
    holder.payload = scrubDirectorySearchDeep(holder.payload);
  }
  return event;
}

// Initialize Sentry only in the prod build
if (SENTRY_DSN && isProdBuild()) {
  Sentry.init({
    dsn: SENTRY_DSN,

    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions

    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration({
        beforeAddRecordingEvent: scrubDirectoryRecordingEvent,
      }),
    ],

    // 100% of sessions in production, 0% in development
    replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 1.0 : 0.0,
    replaysOnErrorSampleRate: 1.0,

    // Set environment
    environment: import.meta.env.MODE || 'development',

    // Enable debug mode in development
    debug: import.meta.env.DEV,

    // Configure beforeSend to filter out certain errors if needed
    beforeSend(event) {
      // A stale chunk is reloading the page. The error is expected and the
      // reloaded page reports its own errors (ROGER-EMR-FRONTEND-9H).
      if (isChunkReloadPending()) return null;
      scrubDirectorySearches(event);
      // Filter out certain errors if needed
      // For example, you might want to filter out network errors
      if (event.exception) {
        const exception = event.exception.values?.[0];
        if (exception?.type === 'NetworkError') {
          return null; // Don't send network errors
        }
      }
      return event;
    },
  });
}

export default Sentry;
